import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { LoadingOverlay } from "cayo.ui";
import { useSchemes } from "@cayo/shared/components/App/hooks";
import { IPropertiesPanel } from "@cayo/shared/components/AppRoots/PropertiesPanelHolder/types";
import ErrorBoundary from "@cayo/shared/components/common/ErrorBoundary";
import { useUIInteraction } from "@cayo/shared/hooks/useUIInteraction";
import logger from "@cayo/shared/libs/logger";
import {
  IPropertiesPanelSignals,
  PropertiesPanelSignalService,
} from "@cayo/shared/services/propertiesPanelSignal.service";
import propertiesPanelUtils from "./utils";
import ErrorModal from "../../Dialogs/ErrorModal";
import Panel from "../../Panel/Panel";
import ObjectSchemeLoader from "../../Schemes/ObjectSchemeLoader";
import PickerSchemeLoader from "../../Schemes/PickerSchemeLoader";
import { AuxSchemeProps } from "../../Schemes/SchemeLoader";

const PropertiesPanelHolder = () => {
  const location = useLocation();
  const signals = useMemo<IPropertiesPanelSignals>(() => new PropertiesPanelSignalService(), []);
  const { loadingInfo, setLoading } = useUIInteraction();

  const {
    propertiesPanels,
    closePropertiesPanel,
    closeAllPropertiesPanels,
    updatePropertiesPanel,
  } = useSchemes();

  useEffect(() => {
    let scheme: IPropertiesPanel;
    if (propertiesPanels.length) {
      scheme = propertiesPanels[propertiesPanels.length - 1];
    }

    const handlers = [
      signals.subscribe("refresh", () => {
        scheme && updatePropertiesPanel(scheme);
        setLoading(false);
      }),
      signals.subscribe("close", closePropertiesPanel),
      signals.subscribe("loading", (loading) => {
        setLoading(loading);
      }),
    ];

    return () => handlers.forEach((h) => h());
  }, [updatePropertiesPanel, closePropertiesPanel]);

  useEffect(() => {
    closeAllPropertiesPanels();
  }, [location.pathname]);

  const generatePropertiesPanels = useCallback(() => {
    return propertiesPanels
      ?.filter((s) => !!s.id)
      ?.map((s, i) => {
        let realId = s.id!;
        const isNewScheme = realId.endsWith("/new") || s.property;
        if (!isNewScheme) {
          realId += "/properties";
        }

        const addProps = {
          onClose: async () => {
            logger.debug("close panel", s?.parentId);

            const closeFunc = () => closePropertiesPanel(s?.parentId);
            closeFunc();
          },
          onRefresh: async () => {
            if (!(s.isNewObject || s.isPicker)) {
              const closeFunc = async () =>
                updatePropertiesPanel({
                  parentId: s?.parentId,
                  id: s.id,
                });

              closeFunc();
            }
          },
        };

        const auxProps: AuxSchemeProps = {
          object: s?.object,
          signals: s?.signals || signals,
          onSubmit: s?.onSubmit,
        };

        logger.debug("render panel", s, realId);
        return (
          <Panel
            key={"panel_" + i}
            isBlocking={s.isNewObject || false}
            type={"medium"}
            isOpen={true}
            {...addProps}
            object={s?.object}
          >
            {s?.isPicker && (
              <PickerSchemeLoader key={propertiesPanelUtils.generateKey(s)} navPropPath={s.id!} />
            )}
            {!s?.isPicker && (
              <ObjectSchemeLoader
                key={propertiesPanelUtils.generateKey(s)}
                schemePath={propertiesPanelUtils.getShemePath(s)}
                auxProps={auxProps}
                //style={s?.isNewObject ? { margin: cayoTheme.metrics.container.margin } : undefined}
              />
            )}
          </Panel>
        );
      });
  }, [propertiesPanels]);

  return (
    <ErrorBoundary
      fallbackComponent={(resetError: () => void) => (
        <ErrorModal
          message={{
            text: "An error occurred while rendering the properties panel",
            severity: "error",
          }}
          onDismiss={function (): void {
            closeAllPropertiesPanels();
            resetError();
          }}
        />
      )}
    >
      {loadingInfo && <LoadingOverlay />}
      {generatePropertiesPanels()}
    </ErrorBoundary>
  );
};

export default PropertiesPanelHolder;
